import { createClient } from '@supabase/supabase-js'

// Environment configuration
const API_BASE = import.meta.env.VITE_API_BASE || '/api'
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY

// Initialize Supabase client for authentication
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY)

// Types for OpenAI API
export interface OpenAIMessage {
  role: 'system' | 'user' | 'assistant'
  content: string
}

export interface OpenAIRequest {
  messages: OpenAIMessage[]
  model?: string
  temperature?: number
  max_tokens?: number
  max_completion_tokens?: number
  response_format?: { type: 'json_object' }
}

export interface OpenAIResponse {
  id: string
  object: string
  created: number
  model: string
  choices: Array<{
    index: number
    message: {
      role: string
      content: string
    }
    finish_reason: string
  }>
  usage: {
    prompt_tokens: number
    completion_tokens: number
    total_tokens: number
  }
}

export interface AIApiError {
  error: string
  details?: string
  retry_after?: number
}

// Get authentication headers
async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  }

  // Always include Authorization header with Supabase anon key (required by Supabase)
  if (SUPABASE_ANON_KEY) {
    headers['Authorization'] = `Bearer ${SUPABASE_ANON_KEY}`
  }

  // Try to get Supabase session first for user authentication
  const { data: { session } } = await supabase.auth.getSession()
  
  if (session?.access_token) {
    // User is authenticated - use their session token
    headers['Authorization'] = `Bearer ${session.access_token}`
  }

  // Always include app key for additional authentication
  const appKey = import.meta.env.VITE_APP_SHARED_KEY
  if (appKey) {
    headers['X-App-Key'] = appKey
  }

  return headers
}

// Main API call function
export async function callOpenAI(
  request: OpenAIRequest,
  timeout: number = 30000
): Promise<OpenAIResponse> {
  const url = API_BASE === '/api' 
    ? '/api/ask'  // Dev mode - use Vite proxy
    : `${API_BASE}/ask`  // Prod mode - direct edge function URL

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeout)

  try {
    const headers = await getAuthHeaders()
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
      signal: controller.signal
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      let errorData: AIApiError
      try {
        errorData = await response.json()
      } catch {
        errorData = {
          error: `HTTP ${response.status}`,
          details: response.statusText
        }
      }

      // Handle specific error cases
      if (response.status === 401) {
        throw new Error('Authentication required. Please sign in.')
      }
      
      if (response.status === 429) {
        const retryAfter = response.headers.get('X-RateLimit-Reset')
        const retryAfterSeconds = retryAfter 
          ? Math.ceil((parseInt(retryAfter) - Date.now()) / 1000)
          : 60
        
        throw new Error(`Rate limit exceeded. Please try again in ${retryAfterSeconds} seconds.`)
      }

      throw new Error(errorData.details || errorData.error || 'API request failed')
    }

    const data: OpenAIResponse = await response.json()
    return data

  } catch (error) {
    clearTimeout(timeoutId)
    
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new Error(`Request timed out after ${timeout / 1000} seconds`)
      }
      throw error
    }
    
    throw new Error('An unexpected error occurred')
  }
}

// Convenience function that matches your existing API call pattern
export async function generateAIContent(
  messages: OpenAIMessage[],
  options: {
    model?: string
    temperature?: number
    max_tokens?: number
    max_completion_tokens?: number
    response_format?: { type: 'json_object' }
    timeout?: number
  } = {}
): Promise<string> {
  const response = await callOpenAI({
    messages,
    model: options.model,
    temperature: options.temperature,
    max_tokens: options.max_tokens,
    max_completion_tokens: options.max_completion_tokens,
    response_format: options.response_format
  }, options.timeout)

  if (!response.choices || response.choices.length === 0) {
    throw new Error('No response content received from AI service')
  }

  return response.choices[0].message.content
}

// Export the main function for backward compatibility
export { callOpenAI as fetchOpenAI }

